import apiService from './api';
import { getErrorMessage, showErrorToast, showSuccessToast, showErrorDialog } from '../utils';
import { getRefreshTimer, isTokenExpired } from '../utils/jwtUtils';
import { API_ENDPOINTS } from '../constants';

class AuthService {
  constructor() {
    this.tokenKey = 'auth_token';
    this.userKey = 'user_data';
    this.refreshTimer = null;
    this.isRefreshing = false;
    this.visibilityHandler = null;
    // Removed BroadcastChannel - each tab now has independent session
  }

  /**
   * Start automatic token refresh timer
   * Refreshes token proactively before expiration
   */
  startAutoRefresh() {
    // Clear existing timer
    this.stopAutoRefresh();

    const token = this.getToken();
    if (!token || isTokenExpired(token)) {
      return;
    }

    // Calculate when to refresh (5 minutes before expiration, or at 80% of lifetime)
    const refreshTime = getRefreshTimer(token, 5 * 60 * 1000);

    if (!refreshTime || refreshTime <= 0) {
      // Token is very close to expiration or already expired, refresh now
      this.attemptRefresh();
      return;
    }

    console.log(`🔄 Auto-refresh scheduled in ${Math.round(refreshTime / 1000)}s`);

    this.refreshTimer = setTimeout(() => {
      this.attemptRefresh();
    }, refreshTime);

    // Pause timer when tab is hidden (performance optimization)
    if (typeof document !== 'undefined') {
      // Remove existing listener if any
      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }

      // Create new handler
      this.visibilityHandler = () => {
        if (document.hidden) {
          // Tab is hidden - clear timer (will restart when tab becomes visible)
          this.stopAutoRefresh();
        } else {
          // Tab is visible - restart timer
          this.startAutoRefresh();
        }
      };

      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  /**
   * Stop automatic token refresh timer
   */
  stopAutoRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    // Remove visibility change listener if it exists
    if (typeof document !== 'undefined' && this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  /**
   * Attempt to refresh token
   */
  async attemptRefresh() {
    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing) {
      console.log('⏳ Token refresh already in progress, skipping...');
      return;
    }

    // Check if we're the active tab (only refresh in active tab)
    if (typeof document !== 'undefined' && document.hidden) {
      console.log('📱 Tab is hidden, skipping refresh (will refresh when visible)');
      return;
    }

    this.isRefreshing = true;

    try {
      const token = await this.refreshToken();

      // Restart timer with new token
      this.startAutoRefresh();

      console.log('✅ Token refreshed automatically');
    } catch (error) {
      console.error('❌ Auto token refresh failed:', error);
      // If refresh fails (token expired), stop timer
      // Don't call logout here - let the 401 handler handle it
      this.stopAutoRefresh();
    } finally {
      this.isRefreshing = false;
    }
  }

  // Login user
  async login(credentials) {
    try {
      // Real API call to backend
      const response = await apiService.post('/auth/login', credentials);

      if (response.success && response.data) {
        const { accessToken, refreshToken, user } = response.data;

        // Merge backend permissions (if present) into user object for immediate availability
        // Ensure permissions object always exists (use empty object if null/undefined)
        const mergedUser = {
          ...user,
          // Backend returns permissions at top-level response.data.permissions
          // Always ensure permissions object exists (default to empty object if null/undefined)
          permissions: response.data.permissions || {},
          // Backend returns accessibleFeatures at top-level response.data.accessibleFeatures
          // Always ensure accessibleFeatures object exists (default to all false if null/undefined)
          accessibleFeatures: response.data.accessibleFeatures || {
            'Dashboard': false,
            'Purchase Order': false,
            'Inventory': false,
            'Supplier': false,
            'Machine': false,
            'Users': false,
            'My Tenant': false,
            'Reports': false
          },
          // Store hierarchy for role-based access control
          hierarchy: user.hierarchy || null,
          // Add computed fields for backward compatibility
          fullName: user.username,
          displayName: user.username,
          name: user.username,
          phone: '',
          email: '',
          bio: ''
        };

        // Store tokens and user data in localStorage
        localStorage.setItem(this.tokenKey, accessToken);
        localStorage.setItem('refresh_token', refreshToken);
        localStorage.setItem(this.userKey, JSON.stringify(mergedUser));

        // Start automatic token refresh timer
        this.startAutoRefresh();

        // Show success message for successful login
        showSuccessToast('Welcome back! Login successful.');

        return { token: accessToken, user: mergedUser };
      } else {
        // Backend returned error in response body
        const errorMessage = getErrorMessage(response.message || 'Login failed');
        this.showLoginErrorDialog(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Login error:', error);

      // Extract error message - handle both Error objects and response objects
      let errorMessage = 'Login failed. Please try again.';

      if (error.message) {
        errorMessage = getErrorMessage(error, errorMessage);
      } else if (error.response?.message) {
        errorMessage = getErrorMessage(error.response.message, errorMessage);
      } else if (typeof error === 'string') {
        errorMessage = getErrorMessage(error, errorMessage);
      }

      this.showLoginErrorDialog(errorMessage);
      throw new Error(errorMessage);
    }
  }

  // Get user's accessible features
  async getUserPermissions() {
    try {
      const response = await apiService.get('/auth/accessible-features');

      if (response.success && response.data) {
        return response.data;
      } else {
        // Return default (all false) if request fails
        return {
          'Dashboard': false,
          'Purchase Order': false,
          'Inventory': false,
          'Supplier': false,
          'Machine': false,
          'Users': false,
          'My Tenant': false,
          'Reports': false
        };
      }
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      // Return default (all false) on error
      return {
        'Dashboard': false,
        'Purchase Order': false,
        'Inventory': false,
        'Supplier': false,
        'Machine': false,
        'Users': false,
        'My Tenant': false,
        'Reports': false
      };
    }
  }

  // Helper method to show login error dialog with appropriate title
  showLoginErrorDialog(errorMessage) {
    let errorTitle = 'Login Error';
    const lowerMessage = errorMessage.toLowerCase();

    if (lowerMessage.includes('username') || lowerMessage.includes('password') ||
      lowerMessage.includes('incorrect') || lowerMessage.includes('invalid credentials')) {
      errorTitle = 'Invalid Credentials';
    } else if (lowerMessage.includes('deactivated') || lowerMessage.includes('disabled')) {
      errorTitle = 'Account Deactivated';
    } else if (lowerMessage.includes('required') || lowerMessage.includes('missing')) {
      errorTitle = 'Missing Information';
    } else if (lowerMessage.includes('not found') || lowerMessage.includes('no account')) {
      errorTitle = 'Account Not Found';
    }

    showErrorDialog(errorMessage, errorTitle, 'OK');
  }

  // Register user
  async register(userData) {
    try {
      const response = await apiService.post('/auth/register', userData);

      if (response.success && response.data) {
        const { token, user } = response.data;

        // Store token and user data in localStorage
        localStorage.setItem(this.tokenKey, token);
        localStorage.setItem(this.userKey, JSON.stringify(user));

        // Show success message for successful registration
        showSuccessToast('Registration successful! Welcome aboard.');

        return { token, user };
      } else {
        const errorMessage = getErrorMessage(response.message || 'Registration failed');
        showErrorToast(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = getErrorMessage(error, 'Registration failed. Please try again.');
      showErrorToast(errorMessage);
      throw new Error(errorMessage);
    }
  }

  // Logout user
  async logout(showToast = true) {
    try {
      const token = this.getToken();
      const refreshToken = localStorage.getItem('refresh_token');

      if (token) {
        // Call backend logout endpoint to blacklist tokens
        // Send refresh token in body so it can be blacklisted
        await apiService.post('/auth/logout', { refreshToken }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      // Show success message for successful logout (only if explicitly requested)
      if (showToast) {
        showSuccessToast('You have been logged out successfully.');
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with local logout even if API call fails
      // Don't show error toast for cleanup scenarios
    } finally {
      // Stop automatic refresh timer
      this.stopAutoRefresh();

      // Always clear local storage
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem('refresh_token');
      localStorage.removeItem(this.userKey);
    }
  }

  // Get current user
  getCurrentUser() {
    try {
      const userData = localStorage.getItem(this.userKey);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  // Get user permissions from localStorage
  getUserPermissions() {
    const user = this.getCurrentUser();
    return user?.permissions || null;
  }

  // Fetch latest permissions from backend
  async fetchCurrentPermissions() {
    try {
      const response = await apiService.get('/auth/permissions');
      if (response.success && response.data) {
        const user = this.getCurrentUser();
        if (user) {
          user.permissions = response.data.permissions;
          user.role = response.data.role;
          user.hierarchy = response.data.hierarchy || user.hierarchy || null;
          localStorage.setItem(this.userKey, JSON.stringify(user));
        }
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch permissions');
    } catch (error) {
      console.error('Error fetching current permissions:', error);
      const errorMessage = getErrorMessage(error, 'Failed to fetch permissions. Please try again.');
      showErrorToast(errorMessage);
      throw new Error(errorMessage);
    }
  }

  // Check if user has specific permission
  hasPermission(permissionKey) {
    const permissions = this.getUserPermissions();
    return permissions && permissions[permissionKey] === 1;
  }

  // Fetch and merge profile data into user object
  async fetchAndMergeProfileData() {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser?.id) {
        return currentUser;
      }

      // Import profileService dynamically to avoid circular dependency
      const profileService = (await import('./profileService')).default;

      // Fetch profile data
      const profile = await profileService.getUserProfile(currentUser.id);

      // Merge profile data into user object
      const mergedUser = {
        ...currentUser,
        ...profile,
        // Update computed fields
        fullName: profile.fullName || currentUser.username,
        displayName: profile.displayName || currentUser.username,
        name: profile.name || currentUser.username,
        phone: profile.phoneNumber || currentUser.phone || '',
        email: profile.email || currentUser.email || '',
        bio: profile.address || currentUser.bio || ''
      };

      // Update localStorage
      localStorage.setItem(this.userKey, JSON.stringify(mergedUser));

      return mergedUser;
    } catch (error) {
      console.error('Error fetching profile data:', error);
      // Return current user if profile fetch fails
      return this.getCurrentUser();
    }
  }

  // Get auth token
  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  // Refresh token
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiService.post('/auth/refresh', { refreshToken });

      if (response.success && response.data) {
        const { accessToken, refreshToken: newRefreshToken } = response.data;

        // Always update access token if provided
        if (accessToken) {
          localStorage.setItem(this.tokenKey, accessToken);
        }

        // Update refresh token if new one provided (might not be provided if token is still valid)
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }

        // Restart automatic refresh timer with new token
        this.startAutoRefresh();

        return accessToken || localStorage.getItem(this.tokenKey);
      } else {
        const errorMessage = getErrorMessage(response.message || 'Token refresh failed');
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      const errorMessage = getErrorMessage(error, 'Session expired. Please login again.');
      // Don't call this.logout() here - let the caller handle logout if needed
      throw new Error(errorMessage);
    }
  }

  // Forgot password
  async forgotPassword(email) {
    try {
      const response = await apiService.post(API_ENDPOINTS.FORGOT_PASSWORD, { email });

      if (response.success) {
        const message = response.message || 'Password reset email sent';
        showSuccessToast(message);
        return message;
      } else {
        const errorMessage = getErrorMessage(response.message || 'Failed to send reset email');
        showErrorToast(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMessage = getErrorMessage(error, 'Failed to send reset email. Please try again.');
      showErrorToast(errorMessage);
      throw new Error(errorMessage);
    }
  }

  // Reset password
  async resetPassword(token, newPassword) {
    try {
      const response = await apiService.post(API_ENDPOINTS.RESET_PASSWORD, {
        token,
        newPassword
      });

      if (response.success) {
        const message = response.message || 'Password reset successfully';
        showSuccessToast(message);
        return message;
      } else {
        const errorMessage = getErrorMessage(response.message || 'Password reset failed');
        showErrorToast(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      const errorMessage = getErrorMessage(error, 'Password reset failed. Please try again.');
      showErrorToast(errorMessage);
      throw new Error(errorMessage);
    }
  }

  // Update user profile
  async updateProfile(userData) {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser?.id) {
        throw new Error('No user logged in');
      }

      // Import profileService dynamically to avoid circular dependency
      const profileService = (await import('./profileService')).default;

      // Update profile using profileService
      const updatedProfile = await profileService.updateProfile(currentUser.id, userData);

      // Update local user data
      const updatedUser = {
        ...currentUser,
        ...updatedProfile,
        // Update computed fields
        fullName: updatedProfile.first_name && updatedProfile.last_name
          ? `${updatedProfile.first_name} ${updatedProfile.last_name}`.trim()
          : currentUser.fullName || currentUser.username,
        displayName: updatedProfile.first_name && updatedProfile.last_name
          ? `${updatedProfile.first_name} ${updatedProfile.last_name}`.trim()
          : currentUser.displayName || currentUser.username,
        name: updatedProfile.first_name && updatedProfile.last_name
          ? `${updatedProfile.first_name} ${updatedProfile.last_name}`.trim()
          : currentUser.name || currentUser.username,
        phone: updatedProfile.phone_number || currentUser.phone || '',
        email: updatedProfile.email || currentUser.email || '',
        bio: updatedProfile.address || currentUser.bio || ''
      };

      localStorage.setItem(this.userKey, JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error('Profile update error:', error);
      const errorMessage = getErrorMessage(error, 'Profile update failed. Please try again.');
      showErrorToast(errorMessage);
      throw new Error(errorMessage);
    }
  }

  // Change password
  async changePassword(currentPassword, newPassword) {
    try {
      const response = await apiService.put('/auth/change-password', {
        currentPassword,
        newPassword
      });

      if (response.success) {
        const message = response.message || 'Password changed successfully';
        showSuccessToast(message);
        return message;
      } else {
        const errorMessage = getErrorMessage(response.message || 'Password change failed');
        showErrorToast(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Change password error:', error);
      const errorMessage = getErrorMessage(error, 'Password change failed. Please try again.');
      showErrorToast(errorMessage);
      throw new Error(errorMessage);
    }
  }

}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;
